import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  debtAdjustments,
  type DebtAdjustmentItem,
} from '@/api/debtAdjustments';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { reports } from '@/api/documents';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import SearchSelect from '@/components/SearchSelect';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { contractLabel } from '@/shared/utils/labels';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

type AdjType = 'MUTUAL_OFFSET' | 'TRANSFER' | 'WRITE_OFF' | 'OFF_BALANCE_WRITEOFF';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  adjustment_type: AdjType;
  use_reserve: boolean;
  record_off_balance: boolean;
  operation_content: string;
  comment: string;
  items: DebtAdjustmentItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  adjustment_type: 'MUTUAL_OFFSET',
  use_reserve: false,
  record_off_balance: false,
  operation_content: '',
  comment: '',
  items: [],
};

const newRow = (n: number, type: AdjType): DebtAdjustmentItem => ({
  line_number: n,
  debtor_contractor_id: '',
  debtor_contract_id: '',
  debtor_account_code: type === 'WRITE_OFF' ? '361' : '361',
  creditor_contractor_id: '',
  creditor_contract_id: '',
  creditor_account_code:
    type === 'WRITE_OFF' ? '944' : type === 'TRANSFER' ? '361' : '631',
  amount: '0',
  content: '',
});

const TYPE_LABEL: Record<AdjType, string> = {
  MUTUAL_OFFSET: 'Взаємозалік',
  TRANSFER: 'Перенос боргу',
  WRITE_OFF: 'Списання боргу',
  OFF_BALANCE_WRITEOFF: 'Списання з 071 (строк давності)',
};

const TYPE_HINT: Record<AdjType, string> = {
  MUTUAL_OFFSET: '',
  TRANSFER:
    'Переносимо борг з однієї пари «контрагент+договір» на іншу. ' +
    '«Дебітор» = звідки переносимо (його борг закривається), ' +
    '«Кредитор» = куди переносимо (у нього виникає такий же борг). ' +
    'Проводка: Дт <рах. дебітора> — Кт <рах. кредитора>. Зазвичай рахунки однакові.',
  WRITE_OFF:
    'Списання безнадійної дебіторки або прощеної кредиторки. ' +
    'У «Дебітор» — контрагент і його рахунок боргу (361/377 для дебіторки, ' +
    '631/685 для прощеної кредиторки). У «Кредитор» — поле договору можна ' +
    'продублювати з дебітора, рахунок = рахунок витрат/доходу (944 для безнадійної ' +
    'дебіторки, 717 для прощеної кредиторки).',
  OFF_BALANCE_WRITEOFF:
    'Остаточне списання з забалансового рахунку 071 після спливу строку ' +
    'давності (3 роки за ПБО). У «Дебітор» — контрагент і договір (та сама пара, ' +
    'яку колись записували при WRITE_OFF з прапором «облік на 071»). ' +
    '«Кредитор» — поля договору продублюйте з дебітора, рахунок = 071. ' +
    'Проводка: Дт 00 — Кт 071.',
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function DebtAdjustmentEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgs.list({ limit: 200 }),
  });
  // UX β: фільтр org dropdown по accounting.manual.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('accounting.manual.manage');
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const docQuery = useQuery({
    queryKey: ['debt-adjustment', id],
    queryFn: () => debtAdjustments.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'debt-adjustment', (cid) => debtAdjustments.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.debt_adjustments', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      // Передзаповнення з query-params (для переходу з акту звірки тощо)
      const qpOrg = searchParams.get('organization_id') || firstOrg;
      const qpType = (searchParams.get('adjustment_type') as AdjType) || 'MUTUAL_OFFSET';
      const qpDebtorCtr = searchParams.get('debtor_contractor_id') || '';
      const qpDebtorCnt = searchParams.get('debtor_contract_id') || '';
      const qpDebtorAcc = searchParams.get('debtor_account_code')
        || (qpType === 'WRITE_OFF' ? '361' : '361');
      const qpAmount = searchParams.get('amount') || '0';
      setForm((f) => {
        if (f.organization_id) return f;
        const initial: DebtAdjustmentItem = {
          ...newRow(1, qpType),
          debtor_contractor_id: qpDebtorCtr,
          debtor_contract_id: qpDebtorCnt,
          debtor_account_code: qpDebtorAcc,
          // Схема вимагає ОБИДВІ сторони: для WRITE_OFF з акта кредитор =
          // той самий контрагент/договір (борг списується по одній парі) —
          // договір допідставиться авто-ефектом нижче, якщо він один.
          creditor_contractor_id: qpDebtorCtr,
          creditor_contract_id: qpDebtorCnt,
          amount: qpAmount,
        };
        return {
          ...f,
          organization_id: qpOrg,
          adjustment_type: qpType,
          items: f.items.length ? f.items : [initial],
        };
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        adjustment_type: (d.adjustment_type as AdjType) ?? 'MUTUAL_OFFSET',
        use_reserve: d.use_reserve ?? false,
        record_off_balance: d.record_off_balance ?? false,
        operation_content: d.operation_content ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          debtor_contractor_id: it.debtor_contractor_id,
          debtor_contract_id: it.debtor_contract_id,
          debtor_account_code: it.debtor_account_code,
          debtor_deal_document_type: it.debtor_deal_document_type,
          debtor_deal_document_id: it.debtor_deal_document_id,
          creditor_contractor_id: it.creditor_contractor_id,
          creditor_contract_id: it.creditor_contract_id,
          creditor_account_code: it.creditor_account_code,
          creditor_deal_document_type: it.creditor_deal_document_type,
          creditor_deal_document_id: it.creditor_deal_document_id,
          amount: String(it.amount),
          content: it.content,
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  // Уникальные contractor_id, по которым нужно подгрузить договоры
  const uniqueContractorIds = useMemo(() => {
    const s = new Set<string>();
    form.items.forEach((it) => {
      if (it.debtor_contractor_id) s.add(it.debtor_contractor_id);
      if (it.creditor_contractor_id) s.add(it.creditor_contractor_id);
    });
    return Array.from(s);
  }, [form.items]);

  const contractQueries = useQueries({
    queries: uniqueContractorIds.map((cid) => ({
      queryKey: ['contracts', form.organization_id, cid],
      queryFn: () =>
        refs.contracts({
          organization_id: form.organization_id,
          contractor_id: cid,
        }),
      enabled: !!form.organization_id && !!cid,
    })),
  });

  const contractsByContractor = useMemo(() => {
    const m: Record<string, { id: string; code: string; name: string }[]> = {};
    uniqueContractorIds.forEach((cid, idx) => {
      const q = contractQueries[idx];
      if (q?.data) m[cid] = q.data;
    });
    return m;
  }, [uniqueContractorIds, contractQueries]);

  // Якщо у контрагента РІВНО ОДИН договір — підставляємо його у порожні
  // поля рядків автоматично (типовий випадок «Основний договір» після
  // міграції; схема вимагає договір з обох сторін — факт 422 2026-08-19).
  useEffect(() => {
    setForm((f) => {
      let changed = false;
      const items = f.items.map((it) => {
        let next = it;
        const dc = contractsByContractor[it.debtor_contractor_id];
        if (it.debtor_contractor_id && !it.debtor_contract_id
            && dc?.length === 1) {
          next = { ...next, debtor_contract_id: dc[0]!.id }; changed = true;
        }
        const cc = contractsByContractor[it.creditor_contractor_id];
        if (it.creditor_contractor_id && !it.creditor_contract_id
            && cc?.length === 1) {
          next = { ...next, creditor_contract_id: cc[0]!.id }; changed = true;
        }
        return next;
      });
      return changed ? { ...f, items } : f;
    });
  }, [contractsByContractor]);

  // Запити сальдо по парах (контрагент+договір+рахунок), які зустрічаються
  // у строках. Один запит на унікальну трійку.
  const balanceKeys = useMemo(() => {
    const set = new Set<string>();
    form.items.forEach((it) => {
      if (it.debtor_contractor_id && it.debtor_contract_id && it.debtor_account_code) {
        set.add(`${it.debtor_contractor_id}|${it.debtor_contract_id}|${it.debtor_account_code}`);
      }
      if (it.creditor_contractor_id && it.creditor_contract_id && it.creditor_account_code) {
        set.add(`${it.creditor_contractor_id}|${it.creditor_contract_id}|${it.creditor_account_code}`);
      }
    });
    return Array.from(set);
  }, [form.items]);

  const balanceQueries = useQueries({
    queries: balanceKeys.map((k) => {
      const [ctr, cnt, acc] = k.split('|') as [string, string, string];
      return {
        queryKey: ['contractor-balance', form.organization_id, ctr, cnt, acc],
        queryFn: () => reports.contractorBalance({
          organization_id: form.organization_id,
          contractor_id: ctr, contract_id: cnt, account_code: acc,
        }),
        enabled: !!form.organization_id && !!ctr && !!cnt && !!acc,
        staleTime: 30 * 1000,
      };
    }),
  });

  const balanceMap = useMemo(() => {
    const m: Record<string, { debit: string; credit: string; balance_signed: string }> = {};
    balanceKeys.forEach((k, idx) => {
      const q = balanceQueries[idx];
      if (q?.data) m[k] = q.data;
    });
    return m;
  }, [balanceKeys, balanceQueries]);

  function balanceFor(ctr: string, cnt: string, acc: string) {
    if (!ctr || !cnt || !acc) return null;
    return balanceMap[`${ctr}|${cnt}|${acc}`] ?? null;
  }

  const totalAmount = form.items.reduce((s, e) => s + Number(e.amount || 0), 0);

  const create = useMutation({
    mutationFn: () =>
      debtAdjustments.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        adjustment_type: form.adjustment_type,
        use_reserve: form.use_reserve,
        record_off_balance: form.record_off_balance,
        operation_content: form.operation_content || null,
        comment: form.comment || null,
        items: form.items,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['debt-adjustments'] });
      navigate(`/debt-adjustments/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () =>
      debtAdjustments.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        adjustment_type: form.adjustment_type,
        use_reserve: form.use_reserve,
        record_off_balance: form.record_off_balance,
        operation_content: form.operation_content || null,
        comment: form.comment || null,
        items: form.items,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['debt-adjustment', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await debtAdjustments.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        adjustment_type: form.adjustment_type,
        use_reserve: form.use_reserve,
        record_off_balance: form.record_off_balance,
        operation_content: form.operation_content || null,
        comment: form.comment || null,
        items: form.items,
      });
      try {
        await debtAdjustments.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/debt-adjustments/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['debt-adjustments'] });
      navigate(`/debt-adjustments/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: спершу зберегти правки, потім провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await debtAdjustments.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        adjustment_type: form.adjustment_type,
        use_reserve: form.use_reserve,
        record_off_balance: form.record_off_balance,
        operation_content: form.operation_content || null,
        comment: form.comment || null,
        items: form.items,
      });
      await debtAdjustments.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['debt-adjustment', id] });
      qc.invalidateQueries({ queryKey: ['debt-adjustments'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => debtAdjustments.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['debt-adjustment', id] });
      qc.invalidateQueries({ queryKey: ['debt-adjustments'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => debtAdjustments.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['debt-adjustments'] });
      navigate('/debt-adjustments');
    },
  });

  function setItem(i: number, patch: Partial<DebtAdjustmentItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        // При смене контрагента сбрасываем договор — он мог принадлежать
        // другому контрагенту.
        if ('debtor_contractor_id' in patch) {
          next.debtor_contract_id = '';
        }
        if ('creditor_contractor_id' in patch) {
          next.creditor_contract_id = '';
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({
      ...f,
      items: [...f.items, newRow(f.items.length + 1, f.adjustment_type)],
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items
        .filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має accounting.manual.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати коригування заборгованості у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/debt-adjustments')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // Схема вимагає контрагента І договір з обох сторін кожного рядка —
  // перевіряємо ДО запиту, щоб замість сирої 422 було зрозуміле пояснення.
  const validateItems = (): boolean => {
    for (const it of form.items) {
      const miss: string[] = [];
      if (!it.debtor_contractor_id) miss.push('контрагент дебітора');
      if (!it.debtor_contract_id) miss.push('договір дебітора');
      if (!it.creditor_contractor_id) miss.push('контрагент кредитора');
      if (!it.creditor_contract_id) miss.push('договір кредитора');
      if (miss.length) {
        setError(`Рядок ${it.line_number}: не заповнено ${miss.join(', ')}. `
          + 'Для списання боргу кредитор = той самий контрагент і договір, '
          + 'що дебітор.');
        return false;
      }
    }
    setError(null);
    return true;
  };

  // ч.917 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  // ч.917: блок «Параметри списання» — лише для виду «Списання боргу»;
  // номери блоків рахуються з цього масиву (num).
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    ...(form.adjustment_type === 'WRITE_OFF'
      ? [{ id: 'writeoff', title: 'Параметри списання', required: 0, filled: 0 }]
      : []),
    { id: 'items', title: `Рядки: ${TYPE_LABEL[form.adjustment_type]}`, required: 1,
      filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];
  const num = (bid: string) => blocks.findIndex((b) => b.id === bid) + 1;

  return (
    <div className="card">
      {/* ч.917: шапка одним рядком — назва ліворуч, тулбар праворуч. */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? `Нове коригування боргу — ${TYPE_LABEL[form.adjustment_type]}`
            : `Коригування боргу № ${form.document_number} — ${TYPE_LABEL[form.adjustment_type]}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
        entriesDoc={{ type: 'doc.debt_adjustments', id }}
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/debt-adjustments')}
          onSave={() => {
            if (!validateItems()) return;
            (isNew ? create.mutate() : update.mutate());
          }}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => {
            if (!validateItems()) return;
            (isNew ? createAndPost.mutate() : updateAndPost.mutate());
          }}
          postDisabled={!isNew && form.items.length === 0}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => {
            confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); });
          }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/debt-adjustments/${id}/print`)}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/debt-adjustments/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.debt_adjustments', id: isNew ? '' : id! }} />
      }>
      {/* ч.917 — блоками: Основні дані (+ підстава) → Параметри списання
          (лише WRITE_OFF) → Рядки → Коментарі та вкладення. */}
      <DocBlock index={num('main')} title="Основні дані">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 2fr',
          gap: '0.75rem',
        }}
      >
        <div className="field">
          <label>Організація *</label>
          <select
            value={form.organization_id}
            onChange={(e) =>
              setForm({ ...form, organization_id: e.target.value })
            }
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} — {o.code}
              </option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input
            value={form.document_number}
            onChange={(e) =>
              setForm({ ...form, document_number: e.target.value })
            }
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>Дата *</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) =>
              setForm({ ...form, document_date: v })
            }
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>Тип коригування</label>
          <select
            value={form.adjustment_type}
            onChange={(e) =>
              setForm({ ...form, adjustment_type: e.target.value as AdjType })
            }
            disabled={isPosted || (!isNew && form.items.length > 0)}
          >
            <option value="MUTUAL_OFFSET">Взаємозалік</option>
            <option value="TRANSFER">Перенос боргу</option>
            <option value="WRITE_OFF">Списання боргу</option>
            <option value="OFF_BALANCE_WRITEOFF">Списання з 071 (строк давності)</option>
          </select>
        </div>
      </div>
      <div className="field">
        <label>Підстава операції</label>
        <input
          value={form.operation_content}
          onChange={(e) =>
            setForm({ ...form, operation_content: e.target.value })
          }
          disabled={isPosted}
          placeholder="напр., Акт звірки № 5 від ..."
        />
      </div>
      </DocBlock>

      {form.adjustment_type === 'WRITE_OFF' && (
        <DocBlock index={num('writeoff')} title="Параметри списання">
        <div style={{
          padding: '0.5rem 0.75rem', marginBottom: '0.5rem',
          background: '#fef9c3', borderLeft: '3px solid #ca8a04',
          fontSize: '0.85rem',
        }}>
          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.use_reserve}
              onChange={(e) => setForm({ ...form, use_reserve: e.target.checked })}
              disabled={isPosted}
            />
            <span>
              За рахунок резерву сумнівних боргів
              {form.use_reserve && (
                <em style={{ color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                  (рахунок витрат замінюється на рахунок резерву, напр. 381)
                </em>
              )}
            </span>
          </label>
          <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.3rem' }}>
            <input
              type="checkbox"
              checked={form.record_off_balance}
              onChange={(e) => setForm({ ...form, record_off_balance: e.target.checked })}
              disabled={isPosted}
            />
            <span>
              Облік на забалансі (071) — для дебіторки
              <em style={{ color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                додаткова проводка Дт 071 — Кт 00. Зберігається 3 роки за ПБО.
              </em>
            </span>
          </label>
        </div>
        </DocBlock>
      )}

      <DocBlock index={num('items')} title={`Рядки: ${TYPE_LABEL[form.adjustment_type]}`}
        actions={!isPosted ? (
          <button type="button" className="primary" onClick={addRow}>
            + додати рядок
          </button>
        ) : undefined}>
      {TYPE_HINT[form.adjustment_type] && (
        <p
          style={{
            margin: '0 0 0.5rem',
            color: 'var(--text-secondary)',
            fontSize: '0.85rem',
          }}
        >
          {TYPE_HINT[form.adjustment_type]}
        </p>
      )}
      <TchTable
      >
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Дебітор (винен нам)</th>
            <th>Договір</th>
            <th style={{ width: 70 }}>Рах.</th>
            <th>Кредитор (винні ми)</th>
            <th>Договір</th>
            <th style={{ width: 70 }}>Рах.</th>
            <th style={{ width: 130 }}>Сума заліку</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const debtorContracts =
              contractsByContractor[it.debtor_contractor_id] ?? [];
            const creditorContracts =
              contractsByContractor[it.creditor_contractor_id] ?? [];
            return (
              <tr key={i}>
                <td>{it.line_number}</td>
                <td>
                  <SearchSelect
                    items={(contractorsQ.data?.items ?? []).map((c) => ({
                      id: c.id, label: c.name, hint: c.edrpou ?? null,
                    }))}
                    value={it.debtor_contractor_id}
                    onChange={(v) => setItem(i, { debtor_contractor_id: v })}
                    disabled={isPosted}
                  />
                </td>
                <td>
                  <select
                    value={it.debtor_contract_id}
                    onChange={(e) =>
                      setItem(i, { debtor_contract_id: e.target.value })
                    }
                    disabled={isPosted || !it.debtor_contractor_id}
                    style={{ width: '100%' }}
                  >
                    <option value="">—</option>
                    {debtorContracts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {contractLabel(c)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    value={it.debtor_account_code}
                    onChange={(e) =>
                      setItem(i, { debtor_account_code: e.target.value })
                    }
                    disabled={isPosted}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <SearchSelect
                    items={(contractorsQ.data?.items ?? []).map((c) => ({
                      id: c.id, label: c.name, hint: c.edrpou ?? null,
                    }))}
                    value={it.creditor_contractor_id}
                    onChange={(v) => setItem(i, { creditor_contractor_id: v })}
                    disabled={isPosted}
                  />
                </td>
                <td>
                  <select
                    value={it.creditor_contract_id}
                    onChange={(e) =>
                      setItem(i, { creditor_contract_id: e.target.value })
                    }
                    disabled={isPosted || !it.creditor_contractor_id}
                    style={{ width: '100%' }}
                  >
                    <option value="">—</option>
                    {creditorContracts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {contractLabel(c)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    value={it.creditor_account_code}
                    onChange={(e) =>
                      setItem(i, { creditor_account_code: e.target.value })
                    }
                    disabled={isPosted}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    value={it.amount}
                    onChange={(e) => setItem(i, { amount: e.target.value })}
                    disabled={isPosted}
                    style={{ textAlign: 'right', width: '100%' }}
                  />
                  {!isPosted && form.adjustment_type === 'MUTUAL_OFFSET' && (() => {
                    const dB = balanceFor(it.debtor_contractor_id, it.debtor_contract_id, it.debtor_account_code);
                    const cB = balanceFor(it.creditor_contractor_id, it.creditor_contract_id, it.creditor_account_code);
                    if (!dB || !cB) return null;
                    // Для активного 3xx: «винні нам» = balance_signed > 0.
                    // Для пасивного 6xx: «ми винні» = balance_signed < 0.
                    const debtorAmount = it.debtor_account_code.startsWith('6')
                      ? Math.max(0, -Number(dB.balance_signed))
                      : Math.max(0, Number(dB.balance_signed));
                    const creditorAmount = it.creditor_account_code.startsWith('6')
                      ? Math.max(0, -Number(cB.balance_signed))
                      : Math.max(0, Number(cB.balance_signed));
                    const minAmt = Math.min(debtorAmount, creditorAmount);
                    if (minAmt <= 0) return null;
                    return (
                      <button
                        type="button"
                        onClick={() => setItem(i, { amount: minAmt.toFixed(2) })}
                        style={{
                          padding: '0.15rem 0.4rem', fontSize: '0.7rem',
                          marginTop: '0.2rem', width: '100%',
                        }}
                        title="Підставити мінімум з двох сальдо"
                      >
                        = min: {fmt(minAmt)}
                      </button>
                    );
                  })()}
                </td>
                <td>
                  {!isPosted && (
                    <button
                      onClick={() => removeRow(i)}
                      className="danger"
                      style={{ padding: '0.3rem 0.6rem' }}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {form.items.map((it, i) => {
            const dB = balanceFor(it.debtor_contractor_id, it.debtor_contract_id, it.debtor_account_code);
            const cB = balanceFor(it.creditor_contractor_id, it.creditor_contract_id, it.creditor_account_code);
            if (!dB && !cB) return null;
            const fmtBal = (acc: string, bs: string) => {
              const v = Number(bs);
              if (acc.startsWith('6')) {
                return v < 0 ? `ми винні ${fmt(-v)}` : v > 0 ? `винні нам ${fmt(v)}` : 'нуль';
              }
              return v > 0 ? `винні нам ${fmt(v)}` : v < 0 ? `ми винні ${fmt(-v)}` : 'нуль';
            };
            return (
              <tr key={`bal-${i}`} style={{ background: 'var(--primary-soft)', fontSize: '0.75rem' }}>
                <td></td>
                <td colSpan={3} style={{ color: '#0c4a6e' }}>
                  {dB ? `Поточне сальдо: ${fmtBal(it.debtor_account_code, dB.balance_signed)}` : ''}
                </td>
                <td colSpan={3} style={{ color: '#0c4a6e' }}>
                  {cB ? `Поточне сальдо: ${fmtBal(it.creditor_account_code, cB.balance_signed)}` : ''}
                </td>
                <td colSpan={2}></td>
              </tr>
            );
          })}
          {form.adjustment_type === 'WRITE_OFF' && form.items.map((it, i) => (
            <tr key={`fx-${i}`} style={{ background: '#fafbfc', fontSize: '0.8rem' }}>
              <td></td>
              <td colSpan={6} style={{ color: 'var(--text-secondary)' }}>
                <span style={{ marginRight: '0.5rem' }}>↳ Валютний борг (опц.):</span>
                <label style={{ marginRight: '0.5rem' }}>
                  Сума у валюті
                  <input
                    type="number"
                    step="0.01"
                    value={it.currency_amount ?? ''}
                    onChange={(e) => setItem(i, { currency_amount: e.target.value || null })}
                    disabled={isPosted}
                    style={{ marginLeft: '0.3rem', width: 110, textAlign: 'right' }}
                    placeholder="USD"
                  />
                </label>
                <label>
                  Курсова різниця (грн, ± знак)
                  <input
                    type="number"
                    step="0.01"
                    value={it.fx_diff_amount ?? ''}
                    onChange={(e) => setItem(i, { fx_diff_amount: e.target.value || null })}
                    disabled={isPosted}
                    style={{ marginLeft: '0.3rem', width: 130, textAlign: 'right' }}
                    placeholder="0"
                  />
                </label>
              </td>
              <td colSpan={2}></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={7} style={{ textAlign: 'right' }}>
              Усього:
            </td>
            <td style={{ textAlign: 'right' }}>
              {fmt(totalAmount)}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </TchTable>

      {!isPosted && (
        <p
          style={{
            marginTop: '0.75rem',
            color: 'var(--text-secondary)',
            fontSize: '0.85rem',
          }}
        >
          <strong>Як працює:</strong> на кожен рядок при проведенні створюється
          одна проводка{' '}
          <strong>
            Дт &lt;рах. кредитора&gt; — Кт &lt;рах. дебітора&gt;
          </strong>{' '}
          на суму заліку. Типово 631 — 361 (наш борг постачальнику закрито
          проти боргу покупця нам). У регістрі взаєморозрахунків обидві
          сторони отримують «+» — обидва борги зменшено.
        </p>
      )}
      </DocBlock>

      <DocBlock index={num('extra')} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted}
          />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.debt_adjustments" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
